import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Trash2, UserPlus, KeyRound, Pencil, Wallet, IdCard, ChevronRight, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rp } from "@/lib/payroll";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { useIsMobile } from "@/lib/useIsMobile";
import { TID } from "@/constants/testIds";
import ConfirmDialog from "@/components/ConfirmDialog";
import { AccountAvatar } from "@/components/AccountAvatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FIELD =
  "focus-ring h-11 w-full rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3.5 text-[15px] placeholder:text-[#B0B0B8]";
const BTN =
  "flex h-10 items-center justify-center gap-1.5 rounded-full bg-[#0071E3] px-5 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50";

const roleLabel = (t, r) => (r === "admin" ? t("acc.roleAdmin") : r === "viewer" ? t("acc.roleViewer") : t("acc.roleAccounting"));

const GHOST_BTN =
  "inline-flex h-9 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white px-4 text-[13px] font-medium text-[#1D1D1F] transition-all hover:bg-[#F5F5F7] active:scale-[0.98] disabled:opacity-50";

function AccountPhotoEditor({ userId, name, hasPhoto, size = 72, onChanged }) {
  const { t } = useT();
  const [ver, setVer] = useState(0);
  const [has, setHas] = useState(!!hasPhoto);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/users/${userId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHas(true);
      setVer(Date.now());
      toast.success(t("acc.photoUpdated"));
      onChanged?.(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${userId}/photo`);
      setHas(false);
      setVer(Date.now());
      toast.success(t("acc.photoRemoved"));
      onChanged?.(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <AccountAvatar id={userId} name={name} hasPhoto={has} version={ver} size={size} />
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={upload} className="hidden" data-testid="account-photo-input" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="account-photo-upload" className={GHOST_BTN}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {has ? t("acc.photoChange") : t("acc.photoUpload")}
        </button>
        {has && (
          <button type="button" onClick={remove} disabled={busy} data-testid="account-photo-remove" className={`${GHOST_BTN} text-[#FF3B30] hover:bg-red-50`}>
            <Trash2 className="h-4 w-4" /> {t("acc.photoRemove")}
          </button>
        )}
      </div>
    </div>
  );
}

function MyPhotoCard({ user }) {
  const { t } = useT();
  const { refresh } = useAuth();
  return (
    <div data-testid="my-photo-card" className="liquid-glass-card space-y-4 p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <Camera className="h-4 w-4 text-[#0071E3]" />
        <h2 className="text-[16px] font-semibold tracking-tight sm:text-[17px]">{t("acc.photoTitle")}</h2>
      </div>
      <AccountPhotoEditor userId={user.id} name={user.name || user.username} hasPhoto={user.has_photo} onChanged={() => refresh?.()} />
      <p className="text-[12.5px] text-[#8E8E93]">{t("acc.photoHint")}</p>
    </div>
  );
}

export default function AccountCenter() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const body = user && user.role === "jarit" ? <JaritAccount user={user} /> : <AdminAccount user={user} />;
  return (
    <div className="space-y-5">
      {user?.employee_id ? (
        <button
          data-testid="open-my-data"
          onClick={() => navigate("/data-saya")}
          className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/60 bg-gradient-to-br from-[#EAF2FE] to-white p-5 text-left transition-all hover:shadow-md active:scale-[0.99]"
        >
          <span className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#0071E3] text-white"><IdCard className="h-5 w-5" /></span>
            <span className="min-w-0">
              <span className="block text-[15px] font-semibold text-[#1D1D1F]">{t("me.title")}</span>
              <span className="block text-[12.5px] text-[#8E8E93]">{t("me.subtitle")}</span>
            </span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-[#8E8E93]" />
        </button>
      ) : null}
      {body}
    </div>
  );
}

function JaritAccount({ user }) {
  const { t } = useT();
  const [un, setUn] = useState("");
  const [np, setNp] = useState("");
  const [cp, setCp] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const wantUn = un.trim();
    const wantPw = np.trim();
    if (!wantUn && !wantPw) return toast.error(t("acc.fillOne"));
    if (wantPw) {
      if (wantPw.length < 6) return toast.error(t("acc.pw6"));
      if (np !== cp) return toast.error(t("acc.pwMismatch"));
    }
    setBusy(true);
    try {
      await api.post("/auth/request-password-change", {
        ...(wantUn ? { new_username: wantUn } : {}),
        ...(wantPw ? { new_password: wantPw } : {}),
      });
      toast.success(t("acc.reqSent"));
      setUn(""); setNp(""); setCp("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={TID.account.root} className="space-y-8">
      <div>
        <p className="text-[13.5px] font-medium text-[#8E8E93]">{t("acc.tag")}</p>
        <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">{t("acc.title")}</h1>
      </div>

      <MyPhotoCard user={user} />

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
      <div className="liquid-glass-card space-y-4 p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-[#0071E3]" />
          <h2 className="text-[16px] font-semibold tracking-tight sm:text-[17px]">{t("acc.jaritChangeTitle")}</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 rounded-xl bg-[#F2F2F7] p-3 text-[13.5px]">
          <div>
            <p className="label">{t("acc.username")}</p>
            <p className="mt-0.5 font-medium">{user?.username}</p>
          </div>
          <div>
            <p className="label">{t("acc.role")}</p>
            <p className="mt-0.5 font-medium">{roleLabel(t, user?.role)}</p>
          </div>
        </div>
        <p className="text-[13.5px] text-[#8E8E93]">
          {t("acc.noteBefore")}<span className="font-medium text-[#1D1D1F]">{t("acc.noteBold")}</span>{t("acc.noteAfter")}
        </p>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="label block">{t("acc.newUsernameOpt")}</label>
            <input
              data-testid={TID.chpwd.newUsername}
              value={un}
              onChange={(e) => setUn(e.target.value)}
              placeholder={t("acc.newUsernamePh")}
              autoComplete="off"
              className={FIELD}
            />
          </div>
          <div className="space-y-1.5">
            <label className="label block">{t("acc.newPwOpt")}</label>
            <input
              data-testid={TID.chpwd.newPassword}
              type="password"
              value={np}
              onChange={(e) => setNp(e.target.value)}
              placeholder={t("acc.newPwPh")}
              autoComplete="new-password"
              className={FIELD}
            />
            <input
              data-testid={TID.chpwd.confirmPassword}
              type="password"
              value={cp}
              onChange={(e) => setCp(e.target.value)}
              placeholder={t("acc.repeatPwPh")}
              autoComplete="new-password"
              className={FIELD}
            />
          </div>
          <button
            data-testid={TID.chpwd.submit}
            onClick={submit}
            disabled={busy || (!un.trim() && !np.trim())}
            className={BTN}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {t("acc.sendReq")}
          </button>
        </div>
      </div>
        <MySavings />
      </div>
    </div>
  );
}

function MySavings() {
  const { t } = useT();
  const [sav, setSav] = useState(null);
  useEffect(() => {
    api.get("/me/savings").then((r) => setSav(r.data)).catch(() => setSav({ balance: 0, entries: [] }));
  }, []);
  const entries = (sav?.entries || []).slice().reverse();
  return (
    <div data-testid="jarit-savings" className="liquid-glass-card space-y-4 p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <Wallet className="h-4 w-4 text-[#0071E3]" />
        <h2 className="text-[16px] font-semibold tracking-tight sm:text-[17px]">{t("acc.mySavings")}</h2>
        <span className="ml-auto rounded-full bg-[#F2F2F7] px-2.5 py-0.5 text-[11px] font-medium text-[#8E8E93]">{t("acc.viewOnly")}</span>
      </div>
      <div className="rounded-2xl bg-gradient-to-br from-[#0071E3] to-[#3AA0FF] p-4 text-white shadow-[0_10px_26px_rgba(0,113,227,0.28)]">
        <p className="text-[12px] font-medium text-white/80">{t("acc.savBalance")}</p>
        <p className="num mt-1 text-[26px] font-bold leading-tight" data-testid="jarit-savings-balance">Rp {rp(sav?.balance || 0)}</p>
      </div>
      <p className="text-[12.5px] leading-snug text-[#8E8E93]">{t("acc.savNote")}</p>
      {sav === null ? (
        <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
      ) : entries.length === 0 ? (
        <p className="rounded-xl bg-[#F5F5F7] py-6 text-center text-[13px] text-[#8E8E93]">{t("acc.savEmpty")}</p>
      ) : (
        <div className="divide-y divide-black/[0.06]">
          {entries.map((l, i) => (
            <div key={l.id || i} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-[13px] font-medium text-[#1D1D1F]">{l.keterangan || (l.tipe === "masuk" ? t("acc.deposit") : t("acc.withdraw"))}</p>
                <p className="text-[11.5px] text-[#8E8E93]">{l.tanggal}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className={`num text-[13px] font-semibold ${l.tipe === "masuk" ? "text-[#128C3E]" : "text-[#FF3B30]"}`}>
                  {l.tipe === "masuk" ? "+" : "\u2212"}Rp {rp(l.nominal)}
                </p>
                <p className="num text-[11px] text-[#8E8E93]">{t("acc.saldoWord")} Rp {rp(l.saldo)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminAccount({ user }) {
  const { t } = useT();
  const readOnly = user?.role === "viewer";
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [cp, setCp] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ username: "", name: "", email: "", role: "jarit", password: "" });
  const [creating, setCreating] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", username: "", role: "jarit", password: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [employees, setEmployees] = useState([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const changePwd = async () => {
    if (np.length < 6) return toast.error(t("acc.pw6"));
    if (np !== cp) return toast.error(t("acc.pwMismatch"));
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: np });
      toast.success(t("acc.pwUpdated"));
      setCur(""); setNp(""); setCp("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingPwd(false);
    }
  };

  const createUser = async () => {
    setCreating(true);
    try {
      await api.post("/users", form);
      toast.success(t("acc.acctCreated"));
      setAddOpen(false);
      setForm({ username: "", name: "", email: "", role: "jarit", password: "" });
      await loadUsers();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCreating(false);
    }
  };

  const removeUser = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/users/${delTarget.id}`);
      setUsers((u) => u.filter((x) => x.id !== delTarget.id));
      toast.success(t("acc.acctDeleted"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDelTarget(null);
    }
  };

  const openEdit = (u) => {
    setEditForm({ name: u.name || "", email: u.email || "", username: u.username || "", role: u.role, password: "", employee_id: u.employee_id || "", verified: !!u.verified });
    setEditTarget(u);
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    if (editForm.password && editForm.password.length < 6)
      return toast.error(t("acc.pw6"));
    setSavingEdit(true);
    try {
      const body = { name: editForm.name, email: editForm.email, username: editForm.username, role: editForm.role, verified: !!editForm.verified };
      if (editForm.password) body.password = editForm.password;
      const { data } = await api.put(`/users/${editTarget.id}`, body);
      await api.put(`/users/${editTarget.id}/link-employee`, { employee_id: editForm.employee_id || "" });
      setUsers((list) => list.map((x) => (x.id === data.id ? data : x)));
      toast.success(t("acc.acctUpdated"));
      setEditTarget(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const setEF = (k) => (v) => setEditForm((f) => ({ ...f, [k]: v }));

  const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid={TID.account.root} className="space-y-8">
      <div>
        <p className="text-[13.5px] font-medium text-[#8E8E93]">{t("acc.tag")}</p>
        <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">{t("acc.title")}</h1>
      </div>

      {!readOnly && <MyPhotoCard user={user} />}

      {/* Change own password */}
      {!readOnly && (
      <div className="liquid-glass-card space-y-4 p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-[#0071E3]" />
          <h2 className="text-[17px] font-semibold tracking-tight">{t("acc.changeYourPw")}</h2>
        </div>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
          <input
            data-testid={TID.account.curPassword}
            type="password"
            value={cur}
            onChange={(e) => setCur(e.target.value)}
            placeholder={t("acc.curPw")}
            autoComplete="current-password"
            className={FIELD}
          />
          <input
            data-testid={TID.account.newPassword}
            type="password"
            value={np}
            onChange={(e) => setNp(e.target.value)}
            placeholder={t("acc.newPwPh")}
            autoComplete="new-password"
            className={FIELD}
          />
          <input
            data-testid={TID.account.confirmPassword}
            type="password"
            value={cp}
            onChange={(e) => setCp(e.target.value)}
            placeholder={t("acc.confirmPw")}
            autoComplete="new-password"
            className={FIELD}
          />
          </div>
          <button
            data-testid={TID.account.changeSubmit}
            onClick={changePwd}
            disabled={savingPwd || !cur || !np || !cp}
            className={BTN}
          >
            {savingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {t("acc.updatePw")}
          </button>
        </div>
      </div>
      )}

      {/* Manage accounts */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[17px] font-semibold tracking-tight">{t("acc.manage")}</h2>
          {!readOnly && (
          <button data-testid={TID.account.addUser} onClick={() => setAddOpen(true)} className={BTN}>
            <UserPlus className="h-4 w-4" /> {t("acc.newAcct")}
          </button>
          )}
        </div>

        {loading ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" />
          </div>
        ) : isMobile ? (
          <div data-testid={TID.account.userList} className="space-y-3">
            {users.map((u) => (
              <div key={u.id} data-testid={TID.account.userRow(u.id)} className="liquid-glass-card px-4 py-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <AccountAvatar id={u.id} name={u.name || u.username} hasPhoto={u.has_photo} size={42} />
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 truncate text-[15px] font-semibold">{u.name || u.username}{u.verified && <VerifiedBadge size={14} />}</p>
                      <p className="truncate text-[12.5px] text-[#8E8E93]">{u.username}</p>
                      {u.email && <p className="truncate text-[12.5px] text-[#8E8E93]">{u.email}</p>}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {!readOnly && (<>
                    <button
                      data-testid={TID.account.userEdit(u.id)}
                      onClick={() => openEdit(u)}
                      className="grid h-8 w-8 place-items-center rounded-full text-[#8E8E93] transition-colors hover:bg-[#EFF7FE] hover:text-[#0071E3]"
                      aria-label="Edit account"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {u.id !== user?.id && (
                      <button
                        data-testid={TID.account.userDelete(u.id)}
                        onClick={() => setDelTarget(u)}
                        className="grid h-8 w-8 place-items-center rounded-full text-[#B0B0B8] transition-colors hover:bg-red-50 hover:text-red-600"
                        aria-label="Delete account"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    </>)}
                  </div>
                </div>
                <div className="mt-2.5">
                  <span className={`rounded-full px-2.5 py-1 text-[12px] font-medium ${
                    u.role === "admin" ? "bg-[#E7F0FF] text-[#0071E3]" : "bg-[#F2F2F7] text-[#6E6E73]"
                  }`}>
                    {roleLabel(t, u.role)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div data-testid={TID.account.userList} className="liquid-glass-card w-full overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-full table-auto text-[14px]">
                <thead>
                  <tr className="border-b border-black/[0.06] text-[12px] uppercase tracking-wide text-[#8E8E93]">
                    <th className="px-4 py-3 text-left">{t("acc.colName")}</th>
                    <th className="px-4 py-3 text-left">{t("acc.username")}</th>
                    <th className="px-4 py-3 text-left">{t("acc.colEmail")}</th>
                    <th className="px-4 py-3 text-left">{t("acc.role")}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={TID.account.userRow(u.id)} className="border-b border-black/[0.04] last:border-0">
                      <td className="px-4 py-3 font-medium"><span className="flex items-center gap-2.5"><AccountAvatar id={u.id} name={u.name || u.username} hasPhoto={u.has_photo} size={32} />{u.name || "\u2014"}{u.verified && <VerifiedBadge size={14} />}</span></td>
                      <td className="px-4 py-3 text-[#6E6E73]">{u.username}</td>
                      <td className="px-4 py-3 text-[#6E6E73]">{u.email || "\u2014"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-[12px] font-medium ${
                          u.role === "admin" ? "bg-[#E7F0FF] text-[#0071E3]" : "bg-[#F2F2F7] text-[#6E6E73]"
                        }`}>
                          {roleLabel(t, u.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!readOnly && (<>
                          <button
                            data-testid={TID.account.userEdit(u.id)}
                            onClick={() => openEdit(u)}
                            className="rounded-lg p-1.5 text-[#B0B0B8] transition-colors hover:bg-[#EFF7FE] hover:text-[#0071E3]"
                            aria-label="Edit account"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {u.id !== user?.id && (
                            <button
                              data-testid={TID.account.userDelete(u.id)}
                              onClick={() => setDelTarget(u)}
                              className="rounded-lg p-1.5 text-[#B0B0B8] transition-colors hover:bg-red-50 hover:text-red-600"
                              aria-label="Delete account"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                          </>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="gap-0 rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[420px]">
          <DialogHeader className="text-left">
            <DialogTitle className="text-[19px] font-semibold tracking-tight">{t("acc.newAcct")}</DialogTitle>
          </DialogHeader>
          <div className="mt-5 space-y-3">
            <input data-testid={TID.account.newUsername} value={form.username}
              onChange={(e) => setF("username")(e.target.value)} placeholder={t("acc.username")} className={FIELD} />
            <input data-testid={TID.account.newName} value={form.name}
              onChange={(e) => setF("name")(e.target.value)} placeholder={t("acc.fullName")} className={FIELD} />
            <input data-testid={TID.account.newEmail} value={form.email}
              onChange={(e) => setF("email")(e.target.value)} placeholder={t("acc.emailOpt")} className={FIELD} />
            <Select value={form.role} onValueChange={setF("role")}>
              <SelectTrigger data-testid={TID.account.newRole} className={FIELD}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="jarit">{t("acc.roleAccountingLong")}</SelectItem>
                <SelectItem value="viewer">{t("acc.roleViewerLong")}</SelectItem>
                <SelectItem value="admin">{t("acc.roleAdminLong")}</SelectItem>
              </SelectContent>
            </Select>
            <input data-testid={TID.account.newUserPassword} type="password" value={form.password}
              onChange={(e) => setF("password")(e.target.value)} placeholder={t("acc.newPwPh")}
              autoComplete="new-password" className={FIELD} />
            <button data-testid={TID.account.createSubmit} onClick={createUser}
              disabled={creating || !form.username || form.password.length < 6}
              className={`${BTN} w-full`}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("acc.createAcct")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(v) => !v && setDelTarget(null)}
        title={t("acc.delTitle")}
        description={delTarget ? t("acc.delDesc", { u: delTarget.username }) : ""}
        onConfirm={removeUser}
      />

      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent data-testid="account-edit-dialog" className="gap-0 rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[420px]">
          <DialogHeader className="text-left">
            <DialogTitle className="text-[19px] font-semibold tracking-tight">
              {t("acc.editUser", { u: editTarget?.username })}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-5 space-y-3">
            {editTarget && (
              <div className="rounded-xl bg-[#F7F7FB] p-3">
                <p className="label mb-2">{t("acc.photoTitle")}</p>
                <AccountPhotoEditor
                  userId={editTarget.id}
                  name={editForm.name || editTarget.username}
                  hasPhoto={editTarget.has_photo}
                  size={60}
                  onChanged={(h) => {
                    setUsers((list) => list.map((x) => (x.id === editTarget.id ? { ...x, has_photo: h } : x)));
                    setEditTarget((p) => (p ? { ...p, has_photo: h } : p));
                  }}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <label className="label block">{t("acc.username")}</label>
              <input data-testid="account-edit-username" value={editForm.username}
                onChange={(e) => setEF("username")(e.target.value)} placeholder={t("acc.username")} className={FIELD} />
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("acc.fullName")}</label>
              <input data-testid="account-edit-name" value={editForm.name}
                onChange={(e) => setEF("name")(e.target.value)} placeholder={t("acc.fullName")} className={FIELD} />
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("acc.colEmail")}</label>
              <input data-testid="account-edit-email" value={editForm.email}
                onChange={(e) => setEF("email")(e.target.value)} placeholder={t("acc.emailOpt")} className={FIELD} />
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("acc.role")}</label>
              <Select value={editForm.role} onValueChange={setEF("role")}>
                <SelectTrigger data-testid="account-edit-role" className={FIELD}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="jarit">{t("acc.roleAccountingLong")}</SelectItem>
                  <SelectItem value="viewer">{t("acc.roleViewerLong")}</SelectItem>
                  <SelectItem value="admin">{t("acc.roleAdminLong")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-[#F7F7FB] px-3.5 py-2.5">
              <div className="min-w-0 pr-3">
                <p className="text-[13.5px] font-medium">{t("acc.verified")}</p>
                <p className="text-[12px] text-[#8E8E93]">{t("acc.verifiedHint")}</p>
              </div>
              <Switch data-testid="account-edit-verified" checked={!!editForm.verified} onCheckedChange={(v) => setEF("verified")(v)} />
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("acc.newPwKeep")}</label>
              <input data-testid="account-edit-password" type="password" value={editForm.password}
                onChange={(e) => setEF("password")(e.target.value)} placeholder={t("acc.newPwPlain")}
                autoComplete="new-password" className={FIELD} />
            </div>
            <div className="space-y-1.5">
              <label className="label block">Hubungkan ke Karyawan</label>
              <Select value={editForm.employee_id || "none"} onValueChange={(v) => setEF("employee_id")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="account-edit-link-employee" className={FIELD}>
                  <SelectValue placeholder="Tidak ditautkan" />
                </SelectTrigger>
                <SelectContent className="max-h-64 rounded-xl">
                  <SelectItem value="none">Tidak ditautkan</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.nama}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[12px] text-[#8E8E93]">Akun ini bisa lihat slip gaji, absensi & kasbon karyawan yang ditautkan.</p>
            </div>
            <button data-testid="account-edit-submit" onClick={saveEdit} disabled={savingEdit}
              className={`${BTN} w-full`}>
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />} {t("acc.saveChanges")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
